import type { RegistroCatalogoPersistido } from './banco-catalogos'

type MetadadosAutenticados = Pick<
  RegistroCatalogoPersistido,
  | 'chave'
  | 'catalogo'
  | 'contextoHash'
  | 'versaoProjecao'
  | 'sincronizadoEm'
  | 'ultimoAcessoEm'
>

const TAMANHO_IV_BYTES = 12
const codificador = new TextEncoder()
const decodificador = new TextDecoder()

const codificar = (valor: string): Uint8Array<ArrayBuffer> => {
  const origem = codificador.encode(valor)
  const destino: Uint8Array<ArrayBuffer> = new Uint8Array(
    new ArrayBuffer(origem.byteLength),
  )
  destino.set(origem)
  return destino
}

const criarDadosAdicionais = (
  metadados: MetadadosAutenticados,
): Uint8Array<ArrayBuffer> => codificar(JSON.stringify([
  metadados.chave,
  metadados.catalogo,
  metadados.contextoHash,
  metadados.versaoProjecao,
  metadados.sincronizadoEm,
  metadados.ultimoAcessoEm,
]))

export const ehChaveCatalogosValida = (
  valor: unknown,
): valor is CryptoKey => {
  if (typeof valor !== 'object' || valor === null) return false
  const chave = valor as CryptoKey
  return chave.type === 'secret'
    && !chave.extractable
    && chave.algorithm.name === 'AES-GCM'
    && 'length' in chave.algorithm
    && chave.algorithm.length === 256
    && chave.usages.length === 2
    && chave.usages.includes('encrypt')
    && chave.usages.includes('decrypt')
}

export const gerarChaveCatalogos = (
  criptografia: Crypto,
): Promise<CryptoKey> => criptografia.subtle.generateKey(
  { name: 'AES-GCM', length: 256 },
  false,
  ['encrypt', 'decrypt'],
)

export const criptografarItensCatalogo = async (
  criptografia: Crypto,
  chave: CryptoKey,
  metadados: MetadadosAutenticados,
  itens: readonly unknown[],
): Promise<Pick<
  RegistroCatalogoPersistido,
  'algoritmo' | 'iv' | 'conteudoCifrado'
>> => {
  const iv: Uint8Array<ArrayBuffer> = new Uint8Array(
    new ArrayBuffer(TAMANHO_IV_BYTES),
  )
  criptografia.getRandomValues(iv)
  const conteudoCifrado = await criptografia.subtle.encrypt({
    name: 'AES-GCM',
    iv,
    additionalData: criarDadosAdicionais(metadados),
  }, chave, codificar(JSON.stringify(itens)))

  return { algoritmo: 'AES-GCM', iv, conteudoCifrado }
}

export const descriptografarItensCatalogo = async (
  criptografia: Crypto,
  chave: CryptoKey,
  registro: RegistroCatalogoPersistido,
): Promise<unknown> => {
  if (registro.iv.byteLength !== TAMANHO_IV_BYTES) {
    throw new Error('Registro de catalogo cifrado invalido')
  }

  const conteudo = await criptografia.subtle.decrypt({
    name: 'AES-GCM',
    iv: registro.iv,
    additionalData: criarDadosAdicionais(registro),
  }, chave, registro.conteudoCifrado)

  return JSON.parse(decodificador.decode(conteudo)) as unknown
}
